import { Flex } from "antd"
import React, { useEffect } from "react"

import { FixedContainer } from "../../components/FixedContainer.tsx"
import { homeColor, homeSize } from "./home-theme.ts"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { BranchSetupFlow } from "../Branches/components/BranchSetupFlow.tsx"
import { CompanyDashboardPage } from "../Branches/pages/CompanyDashboardPage.tsx"
import { selectActiveBranchId, selectIsCompanyMode } from "../Branches/redux/branches.slice.ts"
import { HomeFavoriteReports } from "../ReportsPage/components/home/HomeFavoriteReports.tsx"
import {
  HomeGreeting,
  HomeNotificationsPanel,
  HomeShortcutsAndProjects
} from "./components/HomeSections.tsx"
import { MobileHome } from "./components/MobileHome"
import { WidgetsDashboard } from "./components/Portfolio/WidgetsDashboard.tsx"

// A single operational branch's own scope. `WidgetsDashboard` treats an
// empty/undefined `teamScope` as "every branch the caller can see" (the
// Company HQ no-filter case) — so a branch workspace MUST pass its own id
// here, or every tile silently falls back to the whole company's data,
// identically, whichever branch is selected.
const useHomeTeamScope = () => {
  const activeBranchId = useAppSelector(selectActiveBranchId)
  return activeBranchId ? [activeBranchId] : []
}

/**
 * Home's own canvas.
 *
 * It bleeds out over `#main-content`'s padding and lays its own ~22px gutter
 * instead, so the shell accounts for the sidebar exactly once and Home starts
 * a fixed distance from it however wide (or collapsed) that rail is. No
 * max-width and no `mx-auto`: those were what left a blank column down the
 * left on a wide monitor. No `overflow-x-clip` either — overflow is fixed at
 * the source (`min-w-0` on every grid/flex child) rather than hidden.
 */
const HomeCanvas = ({ children }: React.PropsWithChildren) => (
  <div
    className={"home-canvas -mx-2 lg:-mx-4 -mt-(--main-content-pad-y) min-h-full"}
    style={{
      background: homeColor.canvas,
      boxSizing: "border-box",
      paddingInline: homeSize.contentGutter,
      paddingTop: homeSize.contentGutter - 4,
      paddingBottom: homeSize.contentGutter + 8
    }}
  >
    <Flex
      vertical
      gap={homeSize.sectionGap}
      className={"w-full min-w-0"}
    >
      {children}
    </Flex>
  </div>
)

const WidgetsHome = () => {
  const teamScope = useHomeTeamScope()

  useEffect(() => {
    generateDocumentTitle("Home")
  }, [])

  return (
    <FixedContainer>
      {/* `hidden` would make this a second scroller, stranding the column's bottom reserve as a blank band */}
      <HomeCanvas>
        <BranchSetupFlow />
        {/* The two-column body is `WidgetsDashboard`'s own grid; the sections
            below the fold are handed to it so they land in the right column
            rather than starting a third full-width band under it. */}
        <WidgetsDashboard
          teamScope={teamScope}
          renderHeader={(viewControls, exportControl) => (
            <HomeGreeting
              viewControls={viewControls}
              exportControl={exportControl}
            />
          )}
          mainColumnFooter={
            <>
              <HomeFavoriteReports />
              <HomeShortcutsAndProjects />
            </>
          }
          sideColumnFooter={<HomeNotificationsPanel />}
        />
      </HomeCanvas>
    </FixedContainer>
  )
}

const MobileWidgetsHome = () => {
  useEffect(() => {
    generateDocumentTitle("Home")
  }, [])

  return (
    <FixedContainer>
      <div className={"overflow-x-clip"}>
        <BranchSetupFlow />
        <MobileHome />
      </div>
    </FixedContainer>
  )
}

const HomePage = () => {
  const isMobile = useAppSelector((state) => state.nav.isMobile)
  const isCompanyMode = useAppSelector(selectIsCompanyMode)

  // The company overview replaces this page's content entirely while a
  // multi-branch owner is looking at the whole company — the single-branch
  // operational Home (with its project-creation/search/filter toolbar)
  // stays exactly as it was for every other case, mobile included.
  if (isCompanyMode) return <CompanyDashboardPage />

  return isMobile ? <MobileWidgetsHome /> : <WidgetsHome />
}

export default HomePage
